import { useContext } from 'react';
import { Box, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../index';
import TraitsReconciliationContext from '../../context/TraitsReconciliationContext';
import UnmappedRecordStatus from '../../enums/UnmappedRecordStatus';
import ResolvedTraitCard from './ResolvedTraitCard';
import TraitsRecordCard from './TraitsRecordCard';
import CrioAlert from '../common/CrioAlert';
import ResolvedReconciliationView from './ResolvedReconciliationView';

const StyledErrorAlert = styled(CrioAlert)`
    && {
        position: sticky;
        top: 20px;
        left: 20px;
        z-index: 1000;
        width: inherit;
        margin-bottom: 20px;
    }
`;

interface TraitsWrapperProps {
  isLoadingUnmappedRecords: boolean;
  currentUser: any;
}

export default function UnmappedRecords({ isLoadingUnmappedRecords, currentUser }: Readonly<TraitsWrapperProps>) {
  const {
    currentProcedureConfig, currentProcedureId, currentStudy, loading: loadingConfig, addAsNewTraitsRecord, mapToTraitsRecord, dismissDeletedUnmappableRecord, unmappedRecordsWithStatus, hideResolvedCard, errorMessage, resetErrorMessage,
  } = useContext(TraitsReconciliationContext);

  const { t } = useTranslation();

  if (unmappedRecordsWithStatus === undefined || !currentUser || isLoadingUnmappedRecords) {
    return <LoadingSpinner minimal />;
  }

  const filteredRecords = unmappedRecordsWithStatus
    .filter((record) => record.eSourceStudyId === currentStudy?.studyId && record.traitsStudyProcedureId === currentProcedureId);

  const allRecordsResolved = filteredRecords.length > 0 && filteredRecords.every((record) => record.status === UnmappedRecordStatus.RESOLVED_HIDDEN);

  return (
    <>
      {loadingConfig && !allRecordsResolved && <LoadingSpinner minimal />}
      {errorMessage !== undefined && (
        <StyledErrorAlert
          severity="error"
          title={t('Traits.Error.Title')}
          description={t('Traits.Error.Message')}
          onClose={resetErrorMessage}
        />
      )}
      {allRecordsResolved && <ResolvedReconciliationView />}
      {/* User should never see this state. Just a fallback. */}
      {filteredRecords.length === 0 && !isLoadingUnmappedRecords && (
      <p>No unmapped records found for the selected study and procedure.</p>
      )}
      {!isLoadingUnmappedRecords && currentProcedureConfig
          && filteredRecords.map((record) => (
            <Box key={record.recordId}>
              {record.status === UnmappedRecordStatus.UNMAPPED
                ? <TraitsRecordCard unmappedRecord={record} currentProcedureConfig={currentProcedureConfig} onAddNew={addAsNewTraitsRecord} onMap={mapToTraitsRecord} onDismiss={dismissDeletedUnmappableRecord} />
                : <ResolvedTraitCard type={record.status} onClose={() => hideResolvedCard(record.recordId)} />}
            </Box>
          ))}
    </>
  );
}
